import type { VaultedPaymentMethod } from '@hotjar/webapp/billing/payment';
import type { PaymentData } from '@hotjar/webapp/billing/plan';
import { subscriptionAPI } from '@hotjar/webapp/billing/subscription';
import { globalLoader } from '@hotjar/webapp/components/GlobalLoader/GlobalLoader';
import { supportsMultipleSku } from '@hotjar/webapp/domains/common/product/features';
import { paymentMethodAPI } from '@hotjar/webapp/domains/billing/paymentMethod';
import type { OwnedAccount } from '@hotjar/account';
import { i18next } from '@hotjar/translations';
import { authenticatePaymentMethod as authenticatePaymentMethodWith3DS } from '../braintree/3ds';
import { openPlanChangeConfirmationModal } from '../PlanChangeConfirmation/PlanChangeConfirmation';
import { openSummaryOfChangesModal } from '../PlanChangeConfirmation/SummaryOfChangesWrapper';
import type { BILLING_CYCLE } from '../types';
import { addPaymentMethodAndUpsert } from './addPaymentMethodAndUpsert';
import { getMultipleSkuPlanChanges, getPlanChanges, triggerPlanChanges } from './planChanges';

export interface CheckoutConfig {
  account: OwnedAccount;
  paymentData: PaymentData;
  paymentMethod?: VaultedPaymentMethod;
  billingCycle?: BILLING_CYCLE;
  showPlanChangeConfirmation?: boolean;
}

/**
 * A utility function that gets the subscription updates and enters the customer into the unified checkout flow.
 * This is needed because our checkout has different interfaces within separete modals.
 */
export async function checkout({
  account,
  paymentData,
  paymentMethod: newPaymentMethod,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  billingCycle,
  showPlanChangeConfirmation = false,
}: CheckoutConfig): Promise<unknown> {
  const { costSpecs } = paymentData;
  const multipleSku = supportsMultipleSku(account);

  // function preconditions, where we validate the input to early out
  // before we enter the more heavyweight flows.
  // NOTE: this should technically never happen in TS world, but we are dealing with legacy code too
  if (costSpecs == null) {
    throw new Error(
      'checkout(): You must specify (pass) paymentData which includes costSpecs to use this function.',
    );
  }

  const getPaymentMethod =
    newPaymentMethod !== undefined
      ? Promise.resolve(newPaymentMethod)
      : // If we're not updating the payment method, then attempt to get
        // it from the account.
        //
        // If its not present, that's OK, some operations are permitted
        // without payment methods, such as downgrades, so simply
        // resolve with a `null` payment method.
        paymentMethodAPI.get(account.id as number).catch(() => null);

  globalLoader.show({ message: i18next.t('billing:checkout.processing') });
  const [priceSummary, paymentMethod, planChanges] = await Promise.all([
    // Preview the cost of this change, this will allow us to
    // determine if a payment method is required (such as in the
    // case of an upgrade) or not (in the case of downgrades)
    subscriptionAPI.preview(account, costSpecs, billingCycle),
    // Either get the current payment method of the account, or
    // use the new one provided for the operation. This allows us
    // to update the payment method if its been vaulted elsewhere.
    getPaymentMethod,
    // get a list of changes and what kind of changes took place (i.e. upgrades or downgrades)
    // we will then track these changes on success
    multipleSku ? getMultipleSkuPlanChanges(costSpecs) : getPlanChanges(account, costSpecs),
  ]);

  globalLoader.hide();

  // Meaning, "will payment be required in the future", as we will be renewing
  // for a non-zero amount, we'll have to authenticate that amount with the
  // existing payment method.
  const isPaymentRequired = priceSummary.future.totalWithTax.gt(0);

  const hasPaymentMethod = paymentMethod != null;

  // We can update immediately if there is no future renewal cost (i.e
  // complete) cancellation or if we have a payment method. If there's a
  // renewal cost, and no payment method, then the user will need to enter one.
  const requiresPaymentMethod = isPaymentRequired && !hasPaymentMethod;

  if (requiresPaymentMethod) {
    // If the action we wish to take requires a payment method
    // i.e there's a pro-rata or activation payment, and we
    // don't have a payment method, then we have to request
    // one.
    //
    // Trigger the "update payment method" flow (i.e
    // start with the Company Details modal)
    return addPaymentMethodAndUpsert({
      account,
      paymentData,
      initialBillingCycle: billingCycle,
      planChanges: planChanges.list,
    });
  }

  const inTrialDowngrade = !hasPaymentMethod && !isPaymentRequired;
  if (multipleSku) {
    // We're not showing plan change confirmation for the initial conversion
    //  when customer is not paying anything yet
    const conversionFromFree = priceSummary.current.totalWithTax.eq(0);
    const shouldTriggerChangeModal = !conversionFromFree && !inTrialDowngrade;

    if (showPlanChangeConfirmation && shouldTriggerChangeModal) {
      await openSummaryOfChangesModal({
        costSpecs,
        payNowPrice: priceSummary.charge,
        payLaterPrice: priceSummary.future,
      });
    }
  } else {
    // We're recreating here the old logic of when to show Summary Changes modal
    //  This should be simplified in the future
    const justUpgrades = planChanges.downgradeCount === 0;
    // We don't want to show Plan Change Summary with Churn Survey for in trial downgrades
    const shouldTriggerChangeModal = priceSummary.charge.isProrated || !justUpgrades;
    if (showPlanChangeConfirmation && shouldTriggerChangeModal && !inTrialDowngrade) {
      await openPlanChangeConfirmationModal({
        onChange: () => undefined,
        onCancel: () => undefined,
        opened: true,
        upgradeCount: planChanges.upgradeCount,
        downgradeCount: planChanges.downgradeCount,
        downgradeHighestPlan: planChanges.downgradeHighestPlan,
        payNowPrice: priceSummary.charge,
        payLaterPrice: priceSummary.future,
        isLoading: false,
      });
    }
  }

  // At this stage we may or may not still need to authenticate a payment
  // method, and if we don't have one then we don't authenticate.
  const authenticatePaymentMethod = hasPaymentMethod
    ? // If we're reached this point, then we have a vaulted
      // payment method, and we know how much the Subscription
      // is going to cost. So authenticate the vaulted payment
      // method with the given amount, and then upsert the
      // Subscription with the same changes we previewed.
      //
      // If the authentication amount is <= 0 then no
      // authentication will happen, but this is a concern of
      // the authentication flow.
      //
      // Note: There's a guard deep in authenticatePaymentMethod which
      // will skip authentication if the amount is zero. This invariant
      // should be checked a lot sooner.
      authenticatePaymentMethodWith3DS(
        account,
        // we shouldn't need to cast this, but TS < v4.4 does not yet know
        // that hasPaymentMethod check eliminates null here
        paymentMethod as VaultedPaymentMethod,
        priceSummary.future.totalWithTax,
      )
    : // This might be an error, perhaps we should be more vigilant
      // about what states we can end up here, but ultimately we
      // don't have a payment method to authenticate, and based on
      // the previous conditions we can assume there's nothing to
      // pay for anyway, so resolve with undefined and proceed with
      // the upsert.
      Promise.resolve(undefined);

  globalLoader.show({ message: i18next.t('billing:checkout.contacting-bank') });

  return authenticatePaymentMethod
    .then((authenticatedPaymentMethod: VaultedPaymentMethod | undefined) =>
      subscriptionAPI.upsert({
        accountId: account.id as number,
        costSpecs,
        paymentMethod: authenticatedPaymentMethod,
        billingCycle,
      }),
    )
    .then(
      // Success, we track that there's been plan changes and leave notifications to the caller
      () => triggerPlanChanges(planChanges.list),
      // We were unable to update/create the subscription, show the payment
      // modal again with an error.
      // In this flow, by tradition, we're not showing company details modal
      (error: string) => {
        globalLoader.hide();
        return addPaymentMethodAndUpsert({
          account,
          paymentData,
          initialBillingCycle: billingCycle,
          planChanges: planChanges.list,
          errorMessage: error,
          showCompanyDetailsModal: false,
        });
      },
    )
    .finally(() => globalLoader.hide());
}
